/* ============================================================
   HERO — Find (Yourself)
   Erahaus Shopify Theme for Scent of Qui
   ============================================================ */

(function () {
  'use strict';

  /* ── Scroll Restoration ─────────────────────────────────────── */
  // Prevents the browser from restoring a mid-page scroll position
  // on refresh or back navigation. Only active on pages that include
  // the hero section (homepage).

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  var resetFn = null;

  window.addEventListener('pageshow', function (event) {
    var navEntry = performance.getEntriesByType('navigation')[0];
    var isBackForward = navEntry && navEntry.type === 'back_forward';

    if (event.persisted || isBackForward) {
      /* Call resetFn synchronously — before the browser paints the restored
         frame — so the old character word is never visible for even one frame. */
      if (resetFn) resetFn();

      window.scrollTo(0, 0);
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });

      requestAnimationFrame(function () {
        window.scrollTo(0, 0);
        if (window.ScrollTrigger) ScrollTrigger.refresh(true);
      });
    }
  });

  /* ── Wait for GSAP ──────────────────────────────────────────── */

  function initHero() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(initHero, 100);
      return;
    }
    gsap.registerPlugin(ScrollTrigger);
    setup();
  }

  /* ── DOM References ─────────────────────────────────────────── */

  function setup() {
    const hero         = document.querySelector('.js-hero');
    if (!hero) return;

    const bottleLeft   = hero.querySelector('.js-bottle-left');
    const bottleRight  = hero.querySelector('.js-bottle-right');
    const heroTitle    = hero.querySelector('.js-hero-title');
    const heroWord     = hero.querySelector('.js-hero-word');
    const heroCard     = hero.querySelector('.js-hero-card');
    const cardNameLink = hero.querySelector('.js-card-name-link');
    const cardName     = hero.querySelector('.js-card-name');
    const cardColl     = hero.querySelector('.js-card-collection');

    const characters   = parseCharacters(hero);
    const defaultWord  = heroWord ? heroWord.dataset.default : 'Yourself';
    const isMobile     = window.matchMedia('(max-width: 768px)').matches;

    /* ── Force initial state before ScrollTrigger runs ──────────── */
    /* Ensures the word is "Yourself" and scroll is 0 on any load
       type (navigate, reload, back_forward) before GSAP reads the
       scroll position for the first time. */
    window.scrollTo(0, 0);
    if (heroWord) heroWord.textContent = defaultWord;
    if (heroCard)  gsap.set(heroCard, { opacity: 0, y: 0, scale: 1.1 });

    /* ── Dev warnings — product URL check ──────────────────────── */
    if (location.hostname.indexOf('myshopify.com') !== -1 || location.hostname === 'localhost') {
      characters.forEach(function (char, i) {
        if (!char.product_url) {
          console.warn('[Hero] Character "' + (char.name || '#' + i) + '" is missing product_url — card will not link to a product page.');
        }
      });
    }

    /* ── Lock Word Width ─────────────────────────────────────────── */
    // Measures all words after fonts load and fixes the container width
    // to the widest one — prevents layout shift when words change.

    function lockWordWidth() {
      if (!heroWord) return;

      const probe = document.createElement('span');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = [
        'position: absolute',
        'visibility: hidden',
        'pointer-events: none',
        'white-space: nowrap',
        'font-family: ' + getComputedStyle(heroWord).fontFamily,
        'font-size: '   + getComputedStyle(heroWord).fontSize,
        'font-weight: ' + getComputedStyle(heroWord).fontWeight,
        'letter-spacing: ' + getComputedStyle(heroWord).letterSpacing
      ].join(';');

      document.body.appendChild(probe);

      const allWords = [defaultWord].concat(characters.map(function (c) { return c.name; }));
      var maxWidth = 0;

      allWords.forEach(function (word) {
        probe.textContent = word;
        maxWidth = Math.max(maxWidth, probe.offsetWidth);
      });

      document.body.removeChild(probe);

      heroWord.style.display   = 'inline-block';
      heroWord.style.minWidth  = maxWidth + 'px';
      heroWord.style.textAlign = 'center';
    }

    document.fonts.ready.then(function () {
      lockWordWidth();
      ScrollTrigger.refresh();
    });

    /* ── Entry Animation ────────────────────────────────────────── */
    // Sequence: title scales up → logo fades in → bottles slide in

    const siteLogo   = document.querySelector('.js-site-logo');

    function getSiteHeader() {
      return document.querySelector('.js-site-header');
    }

    const entry = gsap.timeline({
      delay:    0.3,
      defaults: { ease: 'power3.out' },
      onComplete: function () {
        var currentHeader = getSiteHeader();
        if (currentHeader) currentHeader.classList.add('is-visible');
      }
    });

    // Step 1 — Title fades in and scales up
    if (heroTitle) {
      entry.fromTo(
        heroTitle,
        { opacity: 0, scale: 0.92 },
        { opacity: 1, scale: 1, duration: 1.1, ease: 'power2.out' },
        0
      );
    }

    // Step 2 — Logo fades in
    var initialHeader = getSiteHeader();
    if (initialHeader) {
      entry.to(
        initialHeader,
        { opacity: 1, duration: 0.8, ease: 'power2.out' },
        0.7
      );
    }

    // Step 3 — Bottles fade in simultaneously
    if (bottleLeft) {
      entry.fromTo(
        bottleLeft,
        { opacity: 0 },
        { opacity: 1, duration: 1.6, ease: 'power2.out' },
        1.0
      );
    }
    if (bottleRight) {
      entry.fromTo(
        bottleRight,
        { opacity: 0 },
        { opacity: 1, duration: 1.6, ease: 'power2.out' },
        1.0
      );
    }

    /* ── State ──────────────────────────────────────────────────── */

    let activeStep = -1;

    /* ── Character Update ───────────────────────────────────────── */

    function showCharacter(index) {
      if (index === activeStep) return;
      activeStep = index;

      const char = characters[index];
      if (!char || !heroWord) return;

      gsap.to(heroWord, {
        opacity:  0,
        y:        -12,
        duration: var_dur('fast'),
        ease:     'power2.in',
        onComplete: function () {
          heroWord.textContent = char.name;
          gsap.to(heroWord, {
            opacity:  1,
            y:        0,
            duration: var_dur('base'),
            ease:     'power2.out'
          });
        }
      });

      updateCard(char, index === 0);
    }

    /* ── Reset to Default ───────────────────────────────────────── */

    function resetHero() {
      if (activeStep === -1) return;
      activeStep = -1;

      if (!heroWord) return;

      gsap.to(heroWord, {
        opacity:  0,
        y:        12,
        duration: var_dur('fast'),
        ease:     'power2.in',
        onComplete: function () {
          heroWord.textContent = defaultWord;
          gsap.to(heroWord, {
            opacity:  1,
            y:        0,
            duration: var_dur('base'),
            ease:     'power2.out'
          });
        }
      });

      if (heroCard) {
        gsap.to(heroCard, { opacity: 0, y: 12, duration: var_dur('base') });
      }
    }

    /* ── Card Update ────────────────────────────────────────────── */

    function updateCard(char, isFirst) {
      if (!heroCard) return;

      if (isFirst) {
        gsap.fromTo(
          heroCard,
          { opacity: 0, y: 16, scale: 1.1 },
          { opacity: 1, y: 0,  scale: 1.1, duration: var_dur('base'), delay: 0.15, ease: 'power2.out' }
        );
      }

      const textEls = [cardName, cardColl].filter(Boolean);

      gsap.to(textEls, {
        opacity:  0,
        duration: 0.15,
        onComplete: function () {
          if (cardName)   cardName.textContent   = char.product_name;
          if (cardColl)   cardColl.textContent   = char.collection;

          if (cardNameLink && !document.body.classList.contains('is-exhibition-mode')) {
            if (char.product_url) {
              cardNameLink.href = char.product_url;
              cardNameLink.setAttribute('aria-label', 'View ' + char.product_name);
            } else {
              cardNameLink.removeAttribute('href');
              cardNameLink.removeAttribute('aria-label');
            }
          }

          gsap.to(textEls, {
            opacity:  1,
            duration: 0.25,
            stagger:  0.05,
            ease:     'power1.out'
          });
        }
      });
    }

    /* ── Scroll Trigger ─────────────────────────────────────────── */

    if (characters.length === 0) return;

    const totalSteps  = characters.length + 1;
    const scrollDist  = window.innerHeight * (totalSteps * 0.425);

    ScrollTrigger.create({
      trigger:  hero,
      pin:      true,
      start:    'top top',
      end:      '+=' + scrollDist,
      snap: {
        snapTo:   1 / totalSteps,
        duration: { min: 0.4, max: 0.7 },
        ease:     'power1.inOut',
        delay:    0.15
      },
      onUpdate: function (self) {
        const step = Math.round(self.progress * totalSteps);

        if (step <= 0) {
          resetHero();
        } else {
          showCharacter(Math.min(step - 1, characters.length - 1));
        }
      },
    });

    /* ── Hero Exit Transition (scroll down only) ───────────────── */

    var siteWash = document.querySelector('.js-site-wash');

    function getBrandName() {
      return document.querySelector('.js-brand-name');
    }

    ScrollTrigger.create({
      trigger: hero,
      start:   'bottom 50%',
      onEnter: function () {
        var tl = gsap.timeline();

        var fadeEls = [bottleLeft, bottleRight, heroTitle, heroCard].filter(Boolean);
        tl.to(fadeEls, { opacity: 0, duration: 0.4, ease: 'power2.in' }, 0);

        if (siteWash) {
          tl.to(siteWash, { opacity: 1, duration: 0.6, ease: 'power2.inOut' }, 0.05);
        }

        tl.call(function () {
          var currentHeader = getSiteHeader();
          if (currentHeader) {
            currentHeader.classList.add('is-light');
            currentHeader.classList.add('is-scrolled');
          }
        }, null, 0.2);

        var currentBrandName = getBrandName();
        if (currentBrandName) {
          tl.to(currentBrandName, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, 0.4);
        }
      },
    });

    /* ── Hero Re-entry (scroll back up) — fires earlier at bottom 25% ── */

    ScrollTrigger.create({
      trigger: hero,
      start:   'bottom 25%',
      onLeaveBack: function () {
        var fadeEls = [bottleLeft, bottleRight, heroTitle, heroCard].filter(Boolean);
        gsap.killTweensOf(fadeEls);
        gsap.to(fadeEls, { opacity: 1, duration: 0.25, ease: 'power2.out' });

        var currentHeader = getSiteHeader();
        if (currentHeader) {
          currentHeader.classList.remove('is-light');
          currentHeader.classList.remove('is-scrolled');
        }

        if (siteWash) {
          gsap.killTweensOf(siteWash);
          gsap.set(siteWash, { opacity: 0 });
        }

        var currentBrandName = getBrandName();
        if (currentBrandName) {
          gsap.killTweensOf(currentBrandName);
          gsap.to(currentBrandName, { opacity: 0, y: -6, duration: 0.2 });
        }
      },
    });

    window.addEventListener('load', function () { ScrollTrigger.refresh(); });

    /* ── bfcache / back-nav reset ───────────────────────────────── */

    resetFn = function () {
      var els = [bottleLeft, bottleRight, heroTitle, heroCard, heroWord].filter(Boolean);
      if (siteWash)  els.push(siteWash);
      var currentBrandName = getBrandName();
      if (currentBrandName) els.push(currentBrandName);

      gsap.killTweensOf(els);
      var currentHeader = getSiteHeader();
      gsap.killTweensOf(currentHeader);

      if (bottleLeft)  gsap.set(bottleLeft,  { opacity: 1 });
      if (bottleRight) gsap.set(bottleRight, { opacity: 1 });
      if (heroTitle)   gsap.set(heroTitle,   { opacity: 1, scale: 1 });
      if (heroCard)    gsap.set(heroCard,     { opacity: 0, y: 0, scale: 1.1 });
      if (heroWord) {
        heroWord.textContent = defaultWord;
        gsap.set(heroWord, { opacity: 1, y: 0 });
      }
      if (siteWash)  gsap.set(siteWash,  { opacity: 0 });
      if (currentBrandName) gsap.set(currentBrandName, { opacity: 0, y: -6 });

      if (currentHeader) {
        currentHeader.classList.remove('is-light', 'is-scrolled', 'is-footer');
        currentHeader.classList.add('is-visible');
        gsap.set(currentHeader, { opacity: 1 });
      }

      activeStep = -1;
    };
  }

  /* ── Helpers ────────────────────────────────────────────────── */

  function parseCharacters(hero) {
    try {
      const raw = hero.dataset.characters;
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function var_dur(key) {
    const map = { fast: 0.25, base: 0.55, slow: 1.10 };
    return map[key] || 0.55;
  }

  /* ── Init ───────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHero);
  } else {
    initHero();
  }

})();
